'use client'

import { useCallback, useState } from 'react'

import { ErrorItem, HttpStatus, ResponseCode } from '@marketplace-web/core-api/api-client-util'
import {
  removeLocalStorageItem,
  setLocalStorageItem,
} from '@marketplace-web/browser/browser-storage-util'
import {
  submitBusinessRegistrationDsa,
  transformBusinessRegistrationToDto,
  BusinessRegistrationModel,
  REGISTRATION_FORM_STORAGE_KEY,
} from '@marketplace-web/business-accounts/business-accounts-data'

export type OnSubmitRegistrationParams = {
  registrationFields: Partial<BusinessRegistrationModel>
  onSuccess?: () => void
}

const useSubmitRegistration = (): {
  showGeneralError: boolean
  onSubmitRegistration: ({
    registrationFields,
    onSuccess,
  }: OnSubmitRegistrationParams) => Promise<void>
  loading: boolean
  errors: Array<ErrorItem> | null
} => {
  const [showGeneralError, setShowGeneralError] = useState(false)
  const [loading, setLoading] = useState(false)
  const [errors, setErrors] = useState<Array<ErrorItem> | null>(null)

  const onSubmitRegistration = useCallback(
    async ({ registrationFields, onSuccess }: OnSubmitRegistrationParams) => {
      setLocalStorageItem(REGISTRATION_FORM_STORAGE_KEY, JSON.stringify(registrationFields))

      try {
        setErrors(null)
        setShowGeneralError(false)
        setLoading(true)

        const response = await submitBusinessRegistrationDsa(
          transformBusinessRegistrationToDto(registrationFields),
        )

        if ('errors' in response) {
          setErrors(response.errors)

          if (
            response.code === ResponseCode.ServerError ||
            response.status === HttpStatus.InternalError ||
            response.status === HttpStatus.TooManyRequests ||
            response.status === HttpStatus.UnprocessableEntity
          ) {
            setShowGeneralError(true)
          }
        } else {
          onSuccess?.()
          removeLocalStorageItem(REGISTRATION_FORM_STORAGE_KEY)
        }
      } catch (error) {
        setErrors(error)
      } finally {
        setLoading(false)
      }
    },
    [],
  )

  return {
    loading,
    errors,
    showGeneralError,
    onSubmitRegistration,
  }
}

export default useSubmitRegistration
